/**
 * Thread popover content (F-51/F-53/F-54): the message list with author +
 * relative time, @mention highlighting and autocomplete, edit/delete of OWN
 * messages, the reply box, and resolve/re-open. Rendered inside a screen-space
 * `.comment-bubble` by CommentOverlays.
 *
 * All mutations go through the BoardSession comment methods (LOCAL_ORIGIN →
 * undoable, F-43). Authorship gating (edit/delete only your own messages) is
 * a UI rule — the CRDT layer cannot enforce identity in dev-auth mode.
 * Deleting the LAST message deletes the whole thread (shared accessor rule);
 * the overlays close the popover when the thread vanishes.
 */
import type { CommentMessage, CommentThreadInfo, MentionTarget } from "@kola/shared";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useStore } from "zustand";

import { colorForName, currentUser, initialsFor } from "../../core/identity.js";
import type { BoardSession } from "../../core/session.js";
import { CloseButton } from "../../ui/CloseButton.js";
import { showToast } from "../../ui/toast.js";
import { formatRelativeTime, mentionSegments } from "./anchoring.js";
import { RevisionAnalysis } from "./RevisionAnalysis.js";
import {
  applyMention,
  mentionIsMe,
  mentionQueryAt,
  suggestMentions,
  useMentionDirectory,
} from "./mentions.js";

/**
 * docs/07 C4 UX: a reply/edit that lost to a concurrent thread/message
 * deletion must never be silently eaten — the composer has already cleared
 * and the popover is closing (the thread vanished), so the text's last home
 * is a long-lived toast the user can copy it back out of.
 */
function toastLostComment(what: string, body: string): void {
  showToast(`${what} — your text: “${body}”`, { durationMs: 15_000 });
}

/**
 * Comment body with `@mention` tokens highlighted (F-54). A mention that
 * resolves to a real person is styled as a live tag and titled with their
 * name; one that names nobody keeps the plain highlight it has always had, so
 * a roster that has not loaded (or a typo) never makes text disappear.
 */
export function MessageBody({ body }: { body: string }) {
  const directory = useMentionDirectory();
  const meId = currentUser().id;
  return (
    <div className="cm-body">
      {mentionSegments(body, directory).map((segment, i) => {
        if (!segment.mention) return <span key={i}>{segment.text}</span>;
        const known = segment.target !== undefined;
        const me = mentionIsMe(segment.target, meId);
        return (
          <span
            key={i}
            className={`cm-mention${known ? " known" : ""}${me ? " me" : ""}`}
            title={segment.target?.displayName ?? "Not a known teammate"}
          >
            {segment.text}
          </span>
        );
      })}
    </div>
  );
}

export interface CommentInputProps {
  placeholder: string;
  submitLabel: string;
  disabled?: boolean;
  autoFocus?: boolean;
  initialValue?: string;
  onSubmit(body: string): void;
  onCancel?(): void;
}

/**
 * Shared comment textarea: Enter submits (Shift+Enter = newline), the submit
 * button mirrors it. Keydown stops propagation so grid/global shortcuts
 * (C, arrows, Delete…) never fire while typing — Esc is NOT stopped: the
 * overlays' capture handler closes the bubble, which is the wanted behavior.
 *
 * F-54 autocomplete: typing `@` at a word start opens a people picker over the
 * roster. While it is open it OWNS Enter/Tab/arrows/Esc — Enter picks a person
 * instead of posting a half-written comment, and Esc closes the picker instead
 * of the whole bubble (the one case where Esc is stopped). Picking inserts
 * `@handle `; the trailing space is what closes the picker, since a mention
 * query cannot contain whitespace.
 *
 * The picker is a hint, not a gate: the handle is ordinary text, so anyone can
 * type one by hand, and a comment that names nobody is still a valid comment.
 *
 * A row shows avatar + name only, matching rainvic-tool's people picker
 * (components/Review/MentionTextarea.tsx) exactly. The handle used to sit
 * beside the name; since it is derived from the email local part, that second
 * line read as "Kola tags people by email" — and the handle is visible in the
 * textarea the moment a row is picked anyway.
 */
export function CommentInput({
  placeholder,
  submitLabel,
  disabled = false,
  autoFocus = false,
  initialValue = "",
  onSubmit,
  onCancel,
}: CommentInputProps) {
  const [value, setValue] = useState(initialValue);
  /**
   * Caret offset, or null for "not in the text" (blurred, or dismissed with
   * Esc). Null must be its own value rather than a sentinel index: a negative
   * offset would still slice a prefix out of the body and could re-open the
   * picker on an `@` earlier in the line.
   */
  const [caret, setCaret] = useState<number | null>(initialValue.length);
  const [active, setActive] = useState(0);
  /** Set when a pick rewrote the text; applied to the DOM caret after paint. */
  const pendingCaret = useRef<number | null>(null);
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const directory = useMentionDirectory();

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  // The picker is only open when the caret sits in an @token AND somebody
  // matches it — an unmatched query silently closes it rather than showing an
  // empty box that swallows Enter.
  const trigger = caret === null ? null : mentionQueryAt(value, caret);
  const suggestions = trigger === null ? [] : suggestMentions(directory, trigger.query);
  const open = !disabled && suggestions.length > 0;
  const activeIndex = Math.min(active, suggestions.length - 1);

  // Re-anchor the highlight whenever the candidate set changes under it.
  useEffect(() => {
    setActive(0);
  }, [trigger?.start, trigger?.query]);

  // A pick rewrites `value`, so the caret must be restored AFTER React has
  // written the new text — a plain effect would let the browser paint one
  // frame with the caret at the end of the line.
  useLayoutEffect(() => {
    const next = pendingCaret.current;
    if (next === null) return;
    pendingCaret.current = null;
    const el = ref.current;
    if (el === null) return;
    el.focus();
    el.setSelectionRange(next, next);
  }, [value]);

  const syncCaret = (el: HTMLTextAreaElement) => setCaret(el.selectionStart);

  const pick = (target: MentionTarget) => {
    if (trigger === null || caret === null) return;
    const next = applyMention(value, caret, trigger.start, target.handle);
    pendingCaret.current = next.caret;
    setValue(next.text);
    setCaret(next.caret);
  };

  const submit = () => {
    const body = value.trim();
    if (body === "" || disabled) return;
    onSubmit(body);
    setValue("");
    setCaret(0);
  };

  const listboxId = "cm-mention-listbox";

  return (
    <div className="cm-input">
      <div className="cm-input-field">
        {open && (
          <ul className="cm-mention-list" id={listboxId} role="listbox" aria-label="Tag a teammate">
            {suggestions.map((target, i) => (
              <li key={target.uid} role="presentation">
                <button
                  type="button"
                  role="option"
                  id={`cm-mention-option-${i}`}
                  aria-selected={i === activeIndex}
                  className={`cm-mention-option${i === activeIndex ? " active" : ""}`}
                  // Pointer-DOWN, not click: a click would blur the textarea
                  // first, and blur closes the picker.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(target);
                  }}
                  onMouseEnter={() => setActive(i)}
                >
                  {target.avatarUrl ? (
                    <img className="cm-avatar cm-avatar-img" src={target.avatarUrl} alt="" />
                  ) : (
                    <span
                      className="cm-avatar"
                      style={{ background: colorForName(target.displayName) }}
                    >
                      {initialsFor(target.displayName)}
                    </span>
                  )}
                  <span className="cm-mention-name">{target.displayName}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <textarea
          ref={ref}
          className="cm-textarea"
          rows={2}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          spellCheck={false}
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={open ? `cm-mention-option-${activeIndex}` : undefined}
          aria-autocomplete="list"
          onChange={(e) => {
            setValue(e.target.value);
            setCaret(e.target.selectionStart);
          }}
          onSelect={(e) => syncCaret(e.currentTarget)}
          onClick={(e) => syncCaret(e.currentTarget)}
          onBlur={() => setCaret(null)}
          onFocus={(e) => syncCaret(e.currentTarget)}
          onKeyDown={(e) => {
            // While the picker is open it owns Esc too — closing the picker
            // must not also close the bubble the composer lives in.
            if (e.key !== "Escape" || open) e.stopPropagation();
            if (open) {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((i) => (i + 1) % suggestions.length);
                return;
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((i) => (i - 1 + suggestions.length) % suggestions.length);
                return;
              }
              if (e.key === "Enter" || e.key === "Tab") {
                const target = suggestions[activeIndex];
                if (target !== undefined) {
                  e.preventDefault();
                  pick(target);
                  return;
                }
              }
              if (e.key === "Escape") {
                e.preventDefault();
                // Forget the caret entirely: the text is unchanged, so
                // re-deriving the trigger from it would reopen the picker.
                setCaret(null);
                return;
              }
            }
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          onKeyUp={(e) => syncCaret(e.currentTarget)}
        />
      </div>
      <div className="cm-input-actions">
        {onCancel !== undefined && (
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary"
          disabled={disabled || value.trim() === ""}
          onClick={submit}
        >
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

interface MessageRowProps {
  session: BoardSession;
  threadId: string;
  message: CommentMessage;
  frozen: boolean;
  now: number;
}

function MessageRow({ session, threadId, message, frozen, now }: MessageRowProps) {
  const [editing, setEditing] = useState(false);
  const own = message.authorId === session.user.id;
  return (
    <div className="cm-msg">
      <span className="cm-avatar" style={{ background: colorForName(message.authorName) }}>
        {initialsFor(message.authorName)}
      </span>
      <div className="cm-msg-main">
        <div className="cm-msg-head">
          <span className="cm-author">{message.authorName}</span>
          <span className="cm-time">
            {formatRelativeTime(message.ts, now)}
            {message.editedTs !== undefined ? " (edited)" : ""}
          </span>
          {own && !frozen && !editing && (
            <span className="cm-msg-actions">
              <button
                type="button"
                className="cm-link-btn"
                onClick={() => setEditing(true)}
                title="Edit message"
              >
                Edit
              </button>
              <button
                type="button"
                className="cm-link-btn danger"
                onClick={() => session.deleteCommentMessage(threadId, message.id)}
                title="Delete message"
              >
                Delete
              </button>
            </span>
          )}
        </div>
        {editing ? (
          <CommentInput
            placeholder="Edit message"
            submitLabel="Save"
            initialValue={message.body}
            autoFocus
            onSubmit={(body) => {
              if (!session.editCommentMessage(threadId, message.id, body)) {
                // The message (or whole thread) was deleted while editing —
                // deletion wins (C4); keep the text recoverable.
                toastLostComment("Couldn't save the edit: the message was deleted", body);
              }
              setEditing(false);
            }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <MessageBody body={message.body} />
        )}
      </div>
    </div>
  );
}

const iconResolve = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="7" cy="7" r="6" />
    <path d="m4.4 7.2 1.8 1.8 3.4-3.8" />
  </svg>
);

export interface ThreadViewProps {
  session: BoardSession;
  thread: CommentThreadInfo;
  onClose(): void;
}

export function ThreadView({ session, thread, onClose }: ThreadViewProps) {
  const frozen = useStore(session.store, (s) => s.frozen);
  const now = Date.now();
  return (
    <div className="thread-view" role="dialog" aria-label="Comment thread">
      <div className="thread-head">
        {thread.orphaned && (
          <span className="thread-orphan-note" title="The cell or element this comment was attached to was deleted">
            Original location removed
          </span>
        )}
        <span className="spacer" />
        <button
          type="button"
          className={`btn thread-resolve${thread.resolved ? " resolved" : ""}`}
          disabled={frozen}
          onClick={() => session.setCommentThreadResolved(thread.id, !thread.resolved)}
          title={thread.resolved ? "Re-open thread" : "Resolve thread"}
        >
          {iconResolve}
          {thread.resolved ? "Reopen" : "Resolve"}
        </button>
        <CloseButton onClick={onClose} />
      </div>
      <div className="thread-messages">
        {thread.messages.map((message) => (
          <MessageRow
            key={message.id}
            session={session}
            threadId={thread.id}
            message={message}
            frozen={frozen}
            now={now}
          />
        ))}
      </div>
      <RevisionAnalysis key={thread.id} session={session} threadId={thread.id} disabled={frozen} />
      <CommentInput
        placeholder="Reply…"
        submitLabel="Reply"
        disabled={frozen}
        onSubmit={(body) => {
          if (!session.addCommentMessage(thread.id, body)) {
            // Thread deleted concurrently (the popover is closing) — C4:
            // deletion wins, but the reply must stay recoverable.
            toastLostComment("This comment thread was deleted — reply not posted", body);
          }
        }}
      />
    </div>
  );
}
